'use client';

import React from 'react';
import { DISPLAY_DATE_FORMAT_LONG, FleetType } from '@jucy-ui/common';
import { useFormState } from '@jucy-ui/forms';
import Box from '@mui/material/Box';
import { formControlClasses } from '@mui/material/FormControl';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { TripFieldsDateTimeContainer, TripFieldsSeparatorArrow } from '../../fields';
import { SearchFormErrorMessage, SearchFormSubmitButton } from '../components';
import { useGetMaxDate, useGetMinDate, useSearchFormStore, useShouldDisableDate } from '../hooks';
import { jucySearchFormClasses } from '../lib';
import { SearchFormBranchSelect } from './SearchFormBranchSelect';
import { SearchFormDateRangePicker } from './SearchFormDateRangePicker';
import { SearchFormDateTimeLocationContainer } from './SearchFormDateTimeLocationContainer';
import { SearchFormDriveAge } from './SearchFormDriveAge';
import { SearchFormDriveAgePromoCodeField } from './SearchFormDriveAgePromoCodeField';
import { SearchFormFleetTypeField } from './SearchFormFleetTypeField';
import { SearchFormPromoCode } from './SearchFormPromoCode';
import { SearchFormTimeSelect } from './SearchFormTimeSelect';

type SearchFormFieldsProps = {
    shouldDisableFleetType?: (fleetType: FleetType) => boolean;
};

export const SearchFormFields: React.FC<SearchFormFieldsProps> = ({ shouldDisableFleetType }) => {
    const store = useSearchFormStore();
    const form = useFormState();
    const submitError = typeof form.errors.root?.submit?.message === 'string' ? form.errors.root.submit.message : undefined;
    const horizontal = store.variant === 'horizontal';
    return (
        <>
            {store.hiddenFields?.fleetType ? null : (
                <SearchFormFleetTypeField
                    labels={{
                        motorhome: 'Star RV',
                    }}
                    shouldDisableFleetType={shouldDisableFleetType}
                />
            )}
            <SearchFormDateRangePicker
                format={DISPLAY_DATE_FORMAT_LONG}
                minDate={useGetMinDate()}
                maxDate={useGetMaxDate()}
                disablePast={true}
                shouldDisableDate={useShouldDisableDate()}
                disabled={store.disabled}
                className={store.variant}
            >
                {({ startField, endField }) =>
                    horizontal ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(6, 1fr)',
                                gridGap: '8px',
                            }}
                        >
                            {store.hiddenFields?.pickUpBranch ? null : <SearchFormBranchSelect name="pickUpBranch" disabled={store.disabled} />}
                            {store.hiddenFields?.pickUpDate ? null : startField}
                            {store.hiddenFields?.pickUpTime ? null : <SearchFormTimeSelect name="pickUpTime" disabled={store.disabled} />}
                            {store.hiddenFields?.dropOffBranch ? null : <SearchFormBranchSelect name="dropOffBranch" disabled={store.disabled} />}
                            {store.hiddenFields?.dropOffDate ? null : endField}
                            {store.hiddenFields?.dropOffTime ? null : <SearchFormTimeSelect name="dropOffTime" disabled={store.disabled} />}
                            {store.hiddenFields?.driverAge ? null : (
                                <SearchFormDriveAge
                                    disabled={store.disabled}
                                    formGroupOptions={{
                                        sx: {
                                            [`&.${formControlClasses.root}`]: {
                                                gridColumn: '4/4',
                                                marginBottom: 0,
                                            },
                                        },
                                    }}
                                />
                            )}
                            {store.hiddenFields?.promoCode ? null : (
                                <SearchFormPromoCode
                                    disabled={store.disabled}
                                    sx={{
                                        [`&.${formControlClasses.root}`]: {
                                            marginBottom: 0,
                                            gridColumn: '5/5',
                                        },
                                    }}
                                />
                            )}
                            <SearchFormSubmitButton sx={() => ({ gridColumn: '6/6' })} label={store.searchButtonLabel} disabled={store.disabled} />
                        </Box>
                    ) : (
                        <Stack
                            className={jucySearchFormClasses.dateTimeContainer}
                            direction={store.variant === 'small' ? 'column' : 'row'}
                            useFlexGap={true}
                            sx={{
                                gap: 3,
                                width: '100%',
                            }}
                        >
                            <SearchFormDateTimeLocationContainer position="start">
                                {store.hiddenFields?.pickUpBranch ? null : <SearchFormBranchSelect name="pickUpBranch" disabled={store.disabled} />}
                                <TripFieldsDateTimeContainer>
                                    {store.hiddenFields?.pickUpDate ? null : startField}
                                    {store.hiddenFields?.pickUpTime ? null : <SearchFormTimeSelect name="pickUpTime" disabled={store.disabled} />}
                                </TripFieldsDateTimeContainer>
                            </SearchFormDateTimeLocationContainer>
                            <TripFieldsSeparatorArrow variant={store.variant === 'small' ? 'vertical' : 'horizontal'} />
                            <SearchFormDateTimeLocationContainer position="end">
                                {store.hiddenFields?.dropOffBranch ? null : <SearchFormBranchSelect name="dropOffBranch" disabled={store.disabled} />}
                                <TripFieldsDateTimeContainer>
                                    {store.hiddenFields?.dropOffDate ? null : endField}
                                    {store.hiddenFields?.dropOffTime ? null : <SearchFormTimeSelect name="dropOffTime" disabled={store.disabled} />}
                                </TripFieldsDateTimeContainer>
                            </SearchFormDateTimeLocationContainer>
                        </Stack>
                    )
                }
            </SearchFormDateRangePicker>
            {!horizontal ? (
                <Grid
                    className={jucySearchFormClasses.actionsContainer}
                    container
                    spacing={store.variant === 'small' ? 1 : 3}
                    sx={[
                        {
                            position: 'relative',
                            zIndex: 0,
                        },
                        store.variant === 'small'
                            ? {
                                  mt: 3,
                              }
                            : {
                                  mt: 0,
                              },
                    ]}
                >
                    <Grid sx={{ pt: 0 }} size={store.variant === 'small' ? 12 : 6}>
                        {store.hiddenFields?.driverAge && store.hiddenFields.promoCode ? null : (
                            <SearchFormDriveAgePromoCodeField direction={store.variant === 'large' ? 'row' : 'column'} disabled={store.disabled} />
                        )}
                    </Grid>
                    <Grid sx={{ display: 'flex', pt: 0 }} size={store.variant === 'small' ? 12 : 6}>
                        <Box sx={{ ml: 'auto', flex: '0 0 auto', py: 0 }}>
                            <SearchFormSubmitButton label={store.searchButtonLabel} disabled={store.disabled} />
                        </Box>
                    </Grid>
                    {submitError ? (
                        <Grid sx={{ pt: 0, pb: 2 }} size={12}>
                            <SearchFormErrorMessage />
                        </Grid>
                    ) : null}
                </Grid>
            ) : null}
        </>
    );
};
